import React, { useRef, useState } from 'react';
import {
  MinusOutlined,
  HistoryOutlined,
  SettingOutlined,
  ArrowsAltOutlined,
  ShrinkOutlined,
} from '@ant-design/icons';
import { useTheme } from 'styled-components';
import type { ChatMessage, ChatSession, TicketProposal } from '@nxsys/types/models/AiChat';
import {
  StyledChatWindow,
  StyledChatHeader,
  StyledChatHeaderActions,
  StyledHeaderButton,
  StyledChatBody,
  StyledChatBackdrop,
  HideOnMobile,
} from './index.styled';
import MessageList from './MessageList';
import ChatInput from './ChatInput';
import type { ChatInputHandle } from './ChatInput';
import SessionList from './SessionList';
import SessionFeedback from './SessionFeedback';
import LogoSettings from './LogoSettings';
import { LogoRenderer } from './LogoRenderer';
import type { LogoKey } from './LogoRenderer';

export type { LogoKey };

export type SuggestionChip = {
  label: string;
  prompt: string;
  promptTemplate?: string;
};

type Props = {
  isVisible: boolean;
  messages: ChatMessage[];
  sessions: ChatSession[];
  activeSessionId: string | undefined;
  isStreaming: boolean;
  activeToolCall: { name: string; label: string } | null;
  showSessionList: boolean;
  title: string;
  placeholder: string;
  greeting: string;
  isGreetingStreaming?: boolean;
  suggestions: SuggestionChip[];
  quickActions: SuggestionChip[];
  suggestFeedback: boolean;
  ticketProposal: TicketProposal | null;
  isRaisingTicket: boolean;
  onRaiseTicket: (proposal: TicketProposal, attachments?: string[], notes?: string, captureScreen?: boolean) => void;
  onDismissTicket: () => void;
  selectedLogo: LogoKey;
  usePrimaryColor: boolean;
  onClose: () => void;
  onSend: (text: string) => void;
  onSelectSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onNewChat: () => void;
  onToggleSessionList: () => void;
  onSelectLogo: (key: LogoKey) => void;
  onTogglePrimaryColor: (value: boolean) => void;
};

const ChatWindow: React.FC<Props> = ({
  isVisible,
  messages,
  sessions,
  activeSessionId,
  isStreaming,
  activeToolCall,
  showSessionList,
  title,
  placeholder,
  greeting,
  isGreetingStreaming,
  suggestions,
  quickActions,
  suggestFeedback,
  ticketProposal,
  isRaisingTicket,
  onRaiseTicket,
  onDismissTicket,
  selectedLogo,
  usePrimaryColor,
  onClose,
  onSend,
  onSelectSession,
  onDeleteSession,
  onNewChat,
  onToggleSessionList,
  onSelectLogo,
  onTogglePrimaryColor,
}) => {
  const chatInputRef = useRef<ChatInputHandle>(null);
  const [showLogoSettings, setShowLogoSettings] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme() as any;

  const handleFillInput = (text: string) => {
    chatInputRef.current?.fillInput(text);
  };

  return (
    <>
    <StyledChatBackdrop onClick={onClose} aria-hidden="true" />
    <StyledChatWindow $isVisible={isVisible} $expanded={expanded} role="dialog" aria-label={title}>
      <StyledChatHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LogoRenderer logoKey={selectedLogo} size={22} usePrimaryColor={usePrimaryColor} />
          </div>
          <h3 style={{ color: theme.palette.primary.main }}>{title}</h3>
        </div>
        <StyledChatHeaderActions>
          <StyledHeaderButton
            type="text"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => setShowLogoSettings((v) => !v)}
            aria-label="Logo settings"
          />
          <StyledHeaderButton
            type="text"
            size="small"
            icon={<HistoryOutlined />}
            onClick={onToggleSessionList}
            aria-label="Chat history"
          />
          <HideOnMobile>
            <StyledHeaderButton
              type="text"
              size="small"
              icon={expanded ? <ShrinkOutlined /> : <ArrowsAltOutlined />}
              onClick={() => setExpanded((v) => !v)}
              aria-label={expanded ? 'Shrink chat' : 'Expand chat'}
            />
          </HideOnMobile>
          <StyledHeaderButton
            type="text"
            size="small"
            icon={<MinusOutlined style={{ strokeWidth: 60, stroke: 'currentColor' }} />}
            onClick={onClose}
            aria-label="Minimize chat"
          />
        </StyledChatHeaderActions>
      </StyledChatHeader>
      <StyledChatBody>
        <MessageList
          messages={messages}
          isStreaming={isStreaming}
          activeToolCall={activeToolCall}
          greeting={greeting}
          isGreetingStreaming={isGreetingStreaming}
          suggestions={suggestions}
          selectedLogo={selectedLogo}
          usePrimaryColor={usePrimaryColor}
          ticketProposal={ticketProposal}
          isRaisingTicket={isRaisingTicket}
          onRaiseTicket={onRaiseTicket}
          onDismissTicket={onDismissTicket}
          onFillInput={handleFillInput}
          onSend={onSend}
        />
        {activeSessionId && suggestFeedback && !isStreaming && (
          <SessionFeedback
            sessionId={activeSessionId}
          />
        )}
        <SessionList
          isOpen={showSessionList}
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSelectSession={onSelectSession}
          onDeleteSession={onDeleteSession}
          onNewChat={onNewChat}
          onClose={onToggleSessionList}
        />
        <LogoSettings
          isOpen={showLogoSettings}
          selected={selectedLogo}
          usePrimaryColor={usePrimaryColor}
          onSelect={onSelectLogo}
          onTogglePrimaryColor={onTogglePrimaryColor}
          onClose={() => setShowLogoSettings(false)}
        />
      </StyledChatBody>
      <ChatInput
        ref={chatInputRef}
        onSend={onSend}
        disabled={isStreaming}
        placeholder={placeholder}
        suggestions={quickActions}
        allSuggestions={suggestions}
        hasMessages={messages.length > 0}
      />
    </StyledChatWindow>
    </>
  );
};

export default ChatWindow;
